import React, { type JSX, useCallback, useMemo } from 'react';

import { useAutoEnableAiContextualPromptChips } from 'app-components/flux/ai-chat';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import type { FeatureFlagsSimplifi } from 'app-components/flux/feature-flags/featureFlagsSimplifi';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { tracker } from 'app-components/utils/core';

import AiLogoIcon from 'assets/icons/quicken-ai/ai-logo-icon.inline.svg';
import { AILAUNCHER_ICONS } from 'components/AIComponents/AILauncher';
import showConfirmationDialog from 'components/Dialogs/ConfirmationDialog/show';
import { useAppSelector } from 'hooks/redux';
import {
  SettingsFeatureCard,
  SettingsFeatureDisclaimer,
  SettingsFeatureToggleRow,
} from 'containers/Settings/ChildPages/General/SettingsFeatureCard';

import {
  QUICKEN_ASSIST_AI_DISCLAIMER,
  QUICKEN_ASSIST_MASTER_TOGGLE_ID,
  QUICKEN_ASSIST_TOGGLES,
  type QuickenAssistToggleDefinition,
} from './quickenAssistSettingsConfig';

type PreferenceValues = {
  aiInsightsOptIn: boolean | undefined;
  aiContextualPromptChipsEnabled: boolean | undefined;
};

type PreferenceSetters = {
  aiInsightsOptIn: (value: boolean) => void;
  aiContextualPromptChipsEnabled: (value: boolean) => void;
};

const useQuickenAssistPreferences = (): [PreferenceValues, PreferenceSetters] => {
  const [aiInsightsOptIn, setAiInsightsOptIn] = usePreferenceV2('aiInsightsOptIn');
  const [aiContextualPromptChipsEnabled, setAiContextualPromptChipsEnabled] = usePreferenceV2(
    'aiContextualPromptChipsEnabled',
    true,
  );

  const values = useMemo(
    () => ({
      aiInsightsOptIn,
      aiContextualPromptChipsEnabled,
    }),
    [aiInsightsOptIn, aiContextualPromptChipsEnabled],
  );

  const setters = useMemo(
    () => ({
      aiInsightsOptIn: setAiInsightsOptIn,
      aiContextualPromptChipsEnabled: setAiContextualPromptChipsEnabled,
    }),
    [setAiInsightsOptIn, setAiContextualPromptChipsEnabled],
  );

  return [values, setters];
};

const useRolloutFlags = (): Partial<Record<keyof FeatureFlagsSimplifi, boolean>> => {
  const aiContextualPromptChips = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'aiContextualPromptChips'),
  );

  return useMemo(
    () => ({
      aiContextualPromptChips,
    }),
    [aiContextualPromptChips],
  );
};

const isToggleVisible = (
  toggle: QuickenAssistToggleDefinition,
  rolloutFlags: Partial<Record<keyof FeatureFlagsSimplifi, boolean>>,
): boolean => {
  if (!toggle.rolloutFlag) {
    return true;
  }

  return Boolean(rolloutFlags[toggle.rolloutFlag]);
};

const QuickenAssistSettingsCard = (): JSX.Element => {
  useAutoEnableAiContextualPromptChips();

  const [preferenceValues, preferenceSetters] = useQuickenAssistPreferences();
  const rolloutFlags = useRolloutFlags();
  const masterEnabled = Boolean(preferenceValues.aiInsightsOptIn);

  const visibleToggles = useMemo(
    () => QUICKEN_ASSIST_TOGGLES.filter((toggle) => isToggleVisible(toggle, rolloutFlags)),
    [rolloutFlags],
  );

  const handleToggleChange = useCallback(
    (toggle: QuickenAssistToggleDefinition, nextChecked: boolean) => {
      const preferenceKey = toggle.preferenceKey as keyof PreferenceSetters;
      const setPreference = preferenceSetters[preferenceKey];

      const applyChange = () => {
        if (toggle.trackEnable && nextChecked) {
          tracker.track(toggle.trackEnable);
        }
        if (toggle.trackDisable && !nextChecked) {
          tracker.track(
            toggle.trackDisable,
            toggle.trackDisableLocation ? { location: toggle.trackDisableLocation } : undefined,
          );
        }
        setPreference(nextChecked);
      };

      if (!nextChecked && toggle.disableConfirm) {
        showConfirmationDialog({
          title: toggle.disableConfirm.title,
          content: toggle.disableConfirm.content,
          confirmLabel: toggle.disableConfirm.confirmLabel,
          confirmAction: applyChange,
        });
        return;
      }

      applyChange();
    },
    [preferenceSetters],
  );

  return (
    <SettingsFeatureCard
      id='quicken-assist-settings'
      headerIcon={<AiLogoIcon />}
      title='Quicken AI'
      subtitle='Unlock personalized insights and summaries powered by AI.'
      showBetaChip
      footer={<SettingsFeatureDisclaimer>{QUICKEN_ASSIST_AI_DISCLAIMER}</SettingsFeatureDisclaimer>}
    >
      {visibleToggles.map((toggle, index) => {
        const preferenceKey = toggle.preferenceKey as keyof PreferenceValues;
        const Icon = AILAUNCHER_ICONS[toggle.iconName];
        const disabled =
          toggle.requiresMasterEnabled && toggle.id !== QUICKEN_ASSIST_MASTER_TOGGLE_ID && !masterEnabled;
        // When the master toggle is off, sub-features are inactive regardless of their own stored
        // preference, so the row should visually read as off rather than "on but disabled".
        const checked = disabled ? false : Boolean(preferenceValues[preferenceKey]);

        return (
          <SettingsFeatureToggleRow
            key={toggle.id}
            id={toggle.id}
            icon={<Icon />}
            title={toggle.title}
            description={toggle.description}
            checked={checked}
            disabled={disabled}
            showDivider={index > 0}
            onChange={(nextChecked) => handleToggleChange(toggle, nextChecked)}
          />
        );
      })}
    </SettingsFeatureCard>
  );
};

export default QuickenAssistSettingsCard;
